import { useCallback, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSignupViewModel } from "../../viewmodel/useSignupViewModel";
import { useInlineLoading } from "@/shared/loading";
import { routes } from "@/app/navigation/routes";
import styles from "./SignupForm.module.css";
import { PHONE_DIAL_CODE_OPTIONS } from "../../constants/phoneDialCodes";
/**
 * SignupForm Component
 * Pure presentational component - all logic in ViewModel
 */
export const SignupForm = () => {
  const location = useLocation();
  const {
    credentials,
    isLoading,
    firstNameError,
    lastNameError,
    emailError,
    passwordError,
    confirmPasswordError,
    updateFirstName,
    updateLastName,
    updateEmail,
    updatePassword,
    updateConfirmPassword,
    handleSubmit,
    // updateMobile,
    mobileError,
    dialCode,
    setDialCode,
    nationalNumber,
    updateNationalNumber,
  } = useSignupViewModel();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setShowConfirmPassword((prev) => !prev);
  }, []);

  const BUTTON_ID = "signup-button";
  const { isLoading: isInlineLoading } = useInlineLoading(BUTTON_ID);
  const buttonLoading = isLoading || isInlineLoading;

  return (
    <div className={styles.formContainer}>
      <h1 className={styles.title}>Let&apos;s Get Started</h1>
      <p className={styles.subtitle}>Sign up your account</p>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.nameRow}>
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="firstName">
              First Name
            </label>
            <input
              id="firstName"
              type="text"
              className={`${styles.input} ${firstNameError ? styles.inputError : ""}`}
              value={credentials.firstName}
              onChange={(e) => updateFirstName(e.target.value)}
              placeholder="First name"
            />
            {firstNameError && <div className={styles.errorMessage}>{firstNameError}</div>}
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="lastName">
              Last Name
            </label>
            <input
              id="lastName"
              type="text"
              className={`${styles.input} ${lastNameError ? styles.inputError : ""}`}
              value={credentials.lastName}
              onChange={(e) => updateLastName(e.target.value)}
              placeholder="Last name"
            />
            {lastNameError && <div className={styles.errorMessage}>{lastNameError}</div>}
          </div>
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            className={`${styles.input} ${emailError ? styles.inputError : ""}`}
            value={credentials.email}
            onChange={(e) => updateEmail(e.target.value)}
            placeholder="e.g. username@kinety.com"
          />
          {emailError && <div className={styles.errorMessage}>{emailError}</div>}
        </div>
        {/* 
        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="phone">
            Mobile Number
          </label>
          <input
            id="phone"
            type="text"
            className={`${styles.input} ${mobileError ? styles.inputError : ""}`}
            value={credentials.phone}
            onChange={(e) => updateMobile(e.target.value)}
            placeholder="+16067553353"
          />
          {mobileError && <div className={styles.errorMessage}>{mobileError}</div>}
        </div> */}
        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="phone">
            Mobile Number
          </label>

          <div className={styles.phoneFieldGroup}>
            <select
              value={dialCode}
              onChange={(e) => setDialCode(e.target.value)}
              className={styles.dialSelect}
            >
              {PHONE_DIAL_CODE_OPTIONS.map((item) => (
                <option key={item.dialCode} value={item.dialCode}>
                  {item.label}
                </option>
              ))}
            </select>

            <input
              id="phone"
              type="tel"
              className={`${styles.input} ${styles.phoneNumberInput} ${
                mobileError ? styles.inputError : ""
              }`}
              value={nationalNumber}
              maxLength={10}
              minLength={10}
              onChange={(e) => updateNationalNumber(e.target.value)}
              placeholder="Enter mobile number"
            />
          </div>

          {mobileError && <div className={styles.errorMessage}>{mobileError}</div>}
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="password">
            Password
          </label>
          <div className={styles.passwordInputWrapper}>
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              className={`${styles.input} ${styles.passwordInput} ${passwordError ? styles.inputError : ""}`}
              value={credentials.password}
              onChange={(e) => updatePassword(e.target.value)}
              placeholder="enter your password"
            />
            <button
              type="button"
              className={styles.passwordToggle}
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
            >
              {showPassword ? (
                <svg
                  width="15"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg
                  width="15"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
          {passwordError && <div className={styles.errorMessage}>{passwordError}</div>}
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="confirmPassword">
            Confirm Password
          </label>
          <div className={styles.passwordInputWrapper}>
            <input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              className={`${styles.input} ${styles.passwordInput} ${confirmPasswordError ? styles.inputError : ""}`}
              value={credentials.confirmPassword}
              onChange={(e) => updateConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
            />
            <button
              type="button"
              className={styles.passwordToggle}
              onClick={toggleConfirmPasswordVisibility}
              aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
              aria-pressed={showConfirmPassword}
            >
              {showConfirmPassword ? (
                <svg
                  width="15"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg
                  width="15"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
          {confirmPasswordError && (
            <div className={styles.errorMessage}>{confirmPasswordError}</div>
          )}
        </div>

        <button
          id={BUTTON_ID}
          type="submit"
          className={styles.continueButton}
          disabled={buttonLoading}
        >
          {buttonLoading ? "Loading..." : "Continue"}
        </button>
      </form>

      {/* Login Link */}
      <div className={styles.loginLink}>
        Already have an account?
        <Link to={routes.login} state={location.state} className={styles.loginLinkText}>
          Sign In
        </Link>
      </div>
    </div>
  );
};
